import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Users, UserPlus, ClipboardList, Shield, UserX, Search,
  ChevronRight, Check, Clock, AlertTriangle, ArrowLeft, BarChart3,
  DollarSign, Briefcase, XCircle, RotateCcw, Loader2, Plus, Pencil,
  Trash2, LayoutDashboard, FileText, ChevronDown, X, Mail, KeyRound, Copy,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = (path, opts = {}) =>
  fetch(`${BACKEND}/api/employees${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", "x-user-role": "admin", "x-user-id": "dirk", ...opts.headers },
  }).then(async (r) => {
    if (!r.ok) {
      let detail = `API error ${r.status}`;
      try { const j = await r.json(); if (j && j.detail) detail = j.detail; } catch {}
      throw new Error(detail);
    }
    return r.json();
  });

const fmt = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const STATUS_COLORS = {
  active: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  onboarding: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  invited: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  suspended: "bg-[#007a9e]/15 text-[#007a9e] dark:text-[#2a9ec0] border-[#007a9e]/30",
  deactivated: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
};

const RISK_COLORS = {
  on_track: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  at_risk: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  overdue: "bg-red-500/15 text-red-600 dark:text-red-400",
};

const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6"];

// ─── Summary Card ───
const SummaryCard = ({ label, value, sub, icon: Icon, accent }) => (
  <Card className={`p-4 border-border ${accent ? "bg-[var(--brand)]/[0.04] border-[var(--brand)]/20" : "bg-card"}`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-foreground/50 uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold text-foreground mt-1">{value}</div>
        {sub && <div className="text-xs text-foreground/40 mt-0.5">{sub}</div>}
      </div>
      {Icon && <Icon className="h-5 w-5 text-foreground/20" />}
    </div>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
//  DASHBOARD TAB (Phase 2C)
// ═══════════════════════════════════════════════════════════════
const DashboardCharts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/dashboard/charts").then(setData).catch(() => toast.error("Failed to load charts")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>;
  if (!data) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
        <p className="font-medium text-foreground">{label || payload[0]?.name}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-foreground/70">{p.name}: {typeof p.value === "number" && p.value > 100 ? fmt.format(p.value) : p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="dashboard-charts">
      <div className="grid gap-4 md:grid-cols-2">
        {/* By Role */}
        <Card className="border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Employees by Role</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.by_role} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" nameKey="name" label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                {data.by_role.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* By Status */}
        <Card className="border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Employees by Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_status} layout="vertical">
              <XAxis type="number" tick={{ fill: "var(--foreground)", opacity: 0.5, fontSize: 11 }} />
              <YAxis dataKey="name" type="category" width={90} tick={{ fill: "var(--foreground)", opacity: 0.6, fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {data.by_status.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Revenue by Assignment Type */}
        <Card className="border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Revenue by Assignment Type</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_assignment_type}>
              <XAxis dataKey="name" tick={{ fill: "var(--foreground)", opacity: 0.5, fontSize: 10 }} />
              <YAxis tick={{ fill: "var(--foreground)", opacity: 0.5, fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top Earners */}
        <Card className="border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Top Revenue Contributors</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.top_earners} layout="vertical">
              <XAxis type="number" tick={{ fill: "var(--foreground)", opacity: 0.5, fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis dataKey="name" type="category" width={110} tick={{ fill: "var(--foreground)", opacity: 0.6, fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill="#22c55e" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* By Department + Onboarding */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Team by Department</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.by_department} cx="50%" cy="50%" outerRadius={70} dataKey="value" nameKey="name" label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                {data.by_department.map((_, i) => <Cell key={i} fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {data.onboarding_progress.length > 0 && (
          <Card className="border-border bg-card p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Onboarding Progress</h3>
            <div className="space-y-3">
              {data.onboarding_progress.map((o, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-foreground/70">{o.name}</span>
                    <span className="text-foreground font-medium">{o.progress}% ({o.completed}/{o.total})</span>
                  </div>
                  <div className="w-full h-2 bg-foreground/10 rounded-full">
                    <div className="h-full bg-[var(--brand)] rounded-full transition-all" style={{ width: `${o.progress}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  EMPLOYEE FORM (Create / Edit)
// ═══════════════════════════════════════════════════════════════
const EmpField = ({ label, field, required, type = "text", placeholder, className = "", form, set, phone }) => (
  <div className={className}>
    <Label className="text-xs font-medium text-foreground/70">{label}{required && " *"}</Label>
    {phone ? (
      <div className="mt-1"><PhoneField value={form[field]} onChange={(v) => set(field, v)} placeholder={placeholder} testid={`emp-${field}`} /></div>
    ) : (
      <Input value={form[field]} onChange={(e) => set(field, e.target.value)} type={type} placeholder={placeholder} className="mt-1 h-9 text-sm border-border bg-background" data-testid={`emp-${field}`} />
    )}
  </div>
);

const WelcomeEmailButton = ({ employee }) => {
  const [sending, setSending] = useState(false);
  const sent = employee.welcome_email_status && employee.welcome_email_status !== "Not Sent";
  const send = async () => {
    setSending(true);
    try {
      const r = await api(`/${employee.id}/welcome-email`, { method: "POST" });
      if (r.delivered) toast.success("Welcome email sent via Resend");
      else toast.success("Welcome email queued (verify a Resend domain to deliver externally)");
    } catch (err) { toast.error(err?.message || "Could not send welcome email"); }
    finally { setSending(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={send} disabled={sending}
      className="h-8 text-xs" data-testid="emp-welcome-email-btn">
      <Mail className="h-3.5 w-3.5 mr-1" />{sending ? "Sending…" : sent ? "Resend Welcome Email" : "Send Welcome Email"}
    </Button>
  );
};

const ResetPasswordButton = ({ employee }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const generate = async () => {
    setLoading(true); setError("");
    try { const r = await api(`/${employee.id}/reset-password`, { method: "POST" }); setResult(r); }
    catch (e) { setError(e?.message || "Reset failed"); }
    finally { setLoading(false); }
  };
  const copy = () => { navigator.clipboard.writeText(result.temp_password); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const close = () => { setOpen(false); setResult(null); setError(""); };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="h-8 text-xs" data-testid="emp-reset-password-btn">
        <KeyRound className="h-3.5 w-3.5 mr-1" />Reset Password
      </Button>
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="reset-password-modal">
          <Card className="w-full max-w-md border-border bg-card p-6">
            {!result ? (
              <>
                <h3 className="text-lg font-semibold text-foreground">Reset Employee Password</h3>
                <p className="mt-2 text-sm text-foreground/60">This will create a new temporary password and require {employee.first_name} to change it at next login. Existing passwords cannot be viewed.</p>
                {error && <div className="mt-3 text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="reset-password-error">{error}</div>}
                <div className="mt-5 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={close} data-testid="reset-password-cancel">Cancel</Button>
                  <Button size="sm" onClick={generate} disabled={loading} className="bg-[var(--brand)] text-white" data-testid="reset-password-generate">{loading ? "Generating…" : "Generate Temporary Password"}</Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-foreground">Temporary password generated</h3>
                <p className="mt-2 text-sm text-foreground/60">Copy it now. For security, it will not be shown again.</p>
                <div className="mt-4 text-xs text-foreground/50">Login email</div>
                <div className="text-sm font-medium text-foreground" data-testid="reset-password-email">{result.login_email}</div>
                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono text-foreground break-all" data-testid="reset-password-temp">{result.temp_password}</code>
                  <Button size="sm" variant="outline" onClick={copy} data-testid="reset-password-copy"><Copy className="h-3.5 w-3.5 mr-1" />{copied ? "Copied" : "Copy"}</Button>
                </div>
                <div className="mt-2 text-[11px] text-foreground/40">Expires in {result.expires_in_hours}h · Employee must change it at next login.</div>
                <div className="mt-5 flex justify-end">
                  <Button size="sm" onClick={close} className="bg-[var(--brand)] text-white" data-testid="reset-password-done">Done</Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </>
  );
};

const DataMaintenancePanel = () => {
  const [busy, setBusy] = useState("");
  const [phrase, setPhrase] = useState("");
  const [empPreview, setEmpPreview] = useState(null);
  const [revPreview, setRevPreview] = useState(null);
  const prodApi = (path, opts = {}) => fetch(`${BACKEND}/api/admin/production${path}`, {
    ...opts, headers: { "Content-Type": "application/json", "x-user-role": "admin", "x-user-id": "dirk", ...(opts.headers || {}) },
  }).then((r) => r.json());
  useEffect(() => {
    prodApi("/reset-employee-demo/preview").then(setEmpPreview).catch(() => {});
    prodApi("/reset-diagnostic-revenue/preview").then(setRevPreview).catch(() => {});
  }, []);
  const runEmpReset = async () => {
    if (!window.confirm("Deactivate ALL employees except Dirk and close their assignments? This zeroes Revenue Managed.")) return;
    setBusy("emp");
    try { const r = await prodApi("/reset-employee-demo", { method: "POST" }); toast.success(`Deactivated ${r.deactivated} employees`); }
    catch { toast.error("Reset failed"); } finally { setBusy(""); }
  };
  const runRevReset = async () => {
    setBusy("rev");
    try {
      const r = await prodApi("/reset-diagnostic-revenue", { method: "POST", body: JSON.stringify({ confirm_phrase: phrase }) });
      if (r.ok) { toast.success("Diagnostic revenue reset to zero"); setPhrase(""); }
      else toast.error(r.detail || "Reset failed");
    } catch { toast.error("Reset failed"); } finally { setBusy(""); }
  };
  return (
    <div className="grid gap-4 md:grid-cols-2 mt-6" data-testid="data-maintenance-panel">
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
        <h3 className="text-sm font-semibold text-foreground">Reset Employee Demo Data</h3>
        <p className="text-xs text-foreground/60 mt-1">Deactivates every employee except Dirk and closes their assignments. {empPreview ? `${empPreview.count} will be deactivated.` : ""}</p>
        <Button size="sm" variant="outline" className="mt-3 h-8 text-xs border-amber-400 text-amber-700" onClick={runEmpReset} disabled={busy === "emp"} data-testid="reset-employee-demo-btn">
          {busy === "emp" ? "Working…" : "Reset Employee Demo Data"}
        </Button>
      </div>
      <div className="rounded-lg border border-red-200 bg-red-50/50 p-4">
        <h3 className="text-sm font-semibold text-foreground">Reset Diagnostic Revenue</h3>
        <p className="text-xs text-foreground/60 mt-1">Flags Diagnostic quotes/licenses/payments as test data (excluded from revenue). Type <b>RESET REVENUE TO ZERO</b> to confirm.</p>
        <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="RESET REVENUE TO ZERO" className="mt-2 h-8 text-xs" data-testid="reset-revenue-phrase" />
        <Button size="sm" variant="outline" className="mt-2 h-8 text-xs border-red-400 text-red-700" onClick={runRevReset} disabled={busy === "rev" || phrase !== "RESET REVENUE TO ZERO"} data-testid="reset-revenue-btn">
          {busy === "rev" ? "Working…" : "Reset Diagnostic Revenue"}
        </Button>
      </div>
    </div>
  );
};

const InactiveCleanupPanel = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const load = () => { setLoading(true); api("/inactive-cleanup/preview").then(setPreview).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const run = async () => {
    setRunning(true);
    try {
      const r = await api("/inactive-cleanup", { method: "POST", body: JSON.stringify({ confirm_phrase: phrase }) });
      toast.success(`Deleted ${r.deleted_employees} employees, ${r.deleted_users} logins, ${r.deleted_invites} invites`);
      setPhrase(""); load();
    } catch (e) { toast.error(e?.message || "Cleanup failed"); }
    finally { setRunning(false); }
  };
  if (loading && !preview) return null;
  const list = preview?.will_delete || [];
  const canRun = phrase.trim() === (preview?.confirm_phrase || "DELETE INACTIVE EMPLOYEES") && list.length > 0 && !running;
  return (
    <div className="rounded-lg border border-red-200 bg-red-50/40 p-4 mt-4" data-testid="inactive-cleanup-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Trash2 className="h-4 w-4 text-red-500" />Delete Inactive Employees</h3>
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={load} data-testid="cleanup-refresh">Refresh</Button>
      </div>
      <p className="mt-1 text-xs text-foreground/60">
        Permanently removes non-active employee records (deactivated, invited-but-never-activated, test/archived) and their related logins, invites and assignments. <b>{preview?.protected}</b> is always preserved.
      </p>
      <div className="mt-3 text-xs">
        <div className="mb-2 text-foreground/60">Will delete <b data-testid="cleanup-count">{list.length}</b> · Kept: {(preview?.kept || []).map((k) => k.email).join(", ") || "—"}</div>
        <div className="max-h-48 overflow-auto rounded border border-border bg-card">
          <table className="w-full text-left">
            <thead className="text-[11px] uppercase text-foreground/45"><tr><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Status</th><th className="p-2">Login</th><th className="p-2">Action</th></tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-t border-border/50" data-testid={`cleanup-row-${c.id}`}>
                  <td className="p-2">{c.name || "—"}</td>
                  <td className="p-2 break-all">{c.email}</td>
                  <td className="p-2">{c.status}</td>
                  <td className="p-2">{c.related_user ? c.related_user.email : "—"}</td>
                  <td className="p-2 text-red-600 font-medium">Delete</td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-foreground/40">No inactive employees to remove.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={preview?.confirm_phrase || "DELETE INACTIVE EMPLOYEES"} className="h-9 text-xs font-mono" data-testid="cleanup-confirm-input" />
        <Button size="sm" onClick={run} disabled={!canRun} className="bg-red-600 hover:bg-red-700 text-white h-9 text-xs whitespace-nowrap" data-testid="cleanup-run-btn">
          {running ? "Deleting…" : "Delete Inactive Employees"}
        </Button>
      </div>
    </div>
  );
};

const EmailResendPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState(false);
  const [recipient, setRecipient] = useState("dirk@589labs.ai");
  const prodApi = (path, opts = {}) => fetch(`${BACKEND}/api/admin/production${path}`, {
    ...opts, headers: { "Content-Type": "application/json", "x-user-role": "admin", "x-user-id": "dirk", ...(opts.headers || {}) },
  }).then(async (r) => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.detail || `API error ${r.status}`); return j; });
  const load = () => prodApi("/email-config").then(setCfg).catch(() => {});
  useEffect(() => { load(); }, []);
  const test = async () => {
    setTesting(true);
    try {
      const r = await prodApi("/test-email", { method: "POST", body: JSON.stringify({ recipient }) });
      toast.success(r.delivered ? `Test email delivered to ${r.recipient}` : "Test email logged (verify a domain to deliver externally)");
      load();
    } catch (e) { toast.error(e?.message || "Test email failed"); load(); }
    finally { setTesting(false); }
  };
  if (!cfg) return null;
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-3 py-1 text-xs"><span className="text-foreground/50">{label}</span><span className="text-foreground/80 text-right break-all">{value}</span></div>
  );
  return (
    <div className="rounded-lg border border-border bg-card p-4 mt-4" data-testid="email-resend-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Mail className="h-4 w-4" />Email / Resend</h3>
        <div className="flex items-center gap-2">
          <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="h-8 w-56 text-xs" placeholder="test recipient" data-testid="resend-test-recipient" />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={test} disabled={testing} data-testid="resend-test-email-btn">{testing ? "Sending…" : "Send Test Email"}</Button>
        </div>
      </div>
      {cfg.is_sandbox_sender && (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="resend-domain-warning">
          The sender domain is not verified in Resend (using sandbox {cfg.sender_domain}). External email delivery will fail until you verify a domain at resend.com/domains and set the sender to it.
        </div>
      )}
      <div className="mt-3 divide-y divide-border/50">
        <Row label="API key configured" value={cfg.api_key_configured ? "Yes" : "No"} />
        <Row label="Sender email" value={cfg.sender_email || "—"} />
        <Row label="Sender domain" value={cfg.sender_domain || "—"} />
        <Row label="Domain verified" value={cfg.is_sandbox_sender ? "No (sandbox)" : (cfg.domain_verified ? "Yes" : "Unknown")} />
        <Row label="Reply-To" value={cfg.reply_to || "—"} />
        <Row label="Expected sender" value={cfg.expected_sender} />
        <Row label="Last successful send" value={cfg.last_success_at ? `${new Date(cfg.last_success_at).toLocaleString()} → ${cfg.last_success_to || ""}` : "None yet"} />
        <Row label="Last failed send" value={cfg.last_failure_at ? `${new Date(cfg.last_failure_at).toLocaleString()} → ${cfg.last_failure_to || ""}` : "None"} />
        {cfg.last_error && <Row label="Last Resend error" value={cfg.last_error} />}
      </div>
    </div>
  );
};

const EmployeeForm = ({ employee, roles, employees, onSave, onCancel }) => {
  const isEdit = !!employee?.id;
  const [form, setForm] = useState({
    first_name: employee?.first_name || "", last_name: employee?.last_name || "",
    preferred_name: employee?.preferred_name || "", email: employee?.email || "",
    phone: employee?.phone || "", title: employee?.title || "",
    role_id: employee?.role_id || (roles[0]?.id || ""), department: employee?.department || "",
    manager_id: employee?.manager_id || "", start_date: employee?.start_date || new Date().toISOString().split("T")[0],
    employment_type: employee?.employment_type || "full_time",
    cost_rate: employee?.cost_rate || 0, billing_rate: employee?.billing_rate || 0,
    notes: employee?.notes || "", onboarding_template_id: "",
    commission_eligible: employee?.commission_eligible || false,
    is_sales_contact: employee?.is_sales_contact || false,
  });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => { if (!isEdit) api("/onboarding/templates").then(setTemplates).catch(() => {}); }, [isEdit]);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.role_id || !form.start_date) {
      toast.error("Please fill required fields: Name, Email, Role, Start Date"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, cost_rate: +form.cost_rate, billing_rate: +form.billing_rate };
      if (isEdit) {
        const { onboarding_template_id, ...updatePayload } = payload;
        await api(`/${employee.id}`, { method: "PUT", body: JSON.stringify(updatePayload) });
        toast.success("Employee updated");
      } else {
        await api("/", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Employee created");
      }
      onSave();
    } catch (err) { toast.error(err?.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="employee-form">
      <div className="flex items-center gap-2 mb-5">
        <Button variant="ghost" size="sm" onClick={onCancel} className="h-8"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <h2 className="text-lg font-semibold text-foreground">{isEdit ? "Edit Employee" : "Add Employee"}</h2>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <EmpField form={form} set={set} label="First Name" field="first_name" required placeholder="John" />
          <EmpField form={form} set={set} label="Last Name" field="last_name" required placeholder="Smith" />
          <EmpField form={form} set={set} label="Preferred Name" field="preferred_name" placeholder="Johnny" />
          <EmpField form={form} set={set} label="Email" field="email" required type="email" placeholder="john@589labs.ai" />
          <EmpField form={form} set={set} label="Phone" field="phone" placeholder="+1 555 0100" phone />
          <EmpField form={form} set={set} label="Job Title" field="title" placeholder="AI Consultant" />
          <div>
            <Label className="text-xs font-medium text-foreground/70">Role *</Label>
            <select value={form.role_id} onChange={(e) => set("role_id", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm text-foreground px-3" data-testid="emp-role_id">
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <EmpField form={form} set={set} label="Department" field="department" placeholder="Consulting" />
          <div>
            <Label className="text-xs font-medium text-foreground/70">Manager</Label>
            <select value={form.manager_id} onChange={(e) => set("manager_id", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm text-foreground px-3" data-testid="emp-manager_id">
              <option value="">— None —</option>
              {employees.filter((e) => e.id !== employee?.id).map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <EmpField form={form} set={set} label="Start Date" field="start_date" required type="date" />
          <div>
            <Label className="text-xs font-medium text-foreground/70">Employment Type</Label>
            <select value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm text-foreground px-3" data-testid="emp-employment_type">
              <option value="full_time">Full Time</option><option value="part_time">Part Time</option><option value="contractor">Contractor</option>
            </select>
          </div>
          <EmpField form={form} set={set} label="Cost Rate ($/hr)" field="cost_rate" type="number" />
          <EmpField form={form} set={set} label="Billing Rate ($/hr)" field="billing_rate" type="number" />
        </div>
        <div className="mt-4">
          <Label className="text-xs font-medium text-foreground/70">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 min-h-[60px] border-border bg-background text-sm" data-testid="emp-notes" />
        </div>
        <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4">
          <div className="text-xs font-semibold text-foreground/70 uppercase tracking-wide mb-3">Sales & Commission Attribution</div>
          <div className="flex flex-col sm:flex-row gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" data-testid="emp-is-sales-contact-label">
              <input type="checkbox" checked={!!form.is_sales_contact} onChange={(e) => set("is_sales_contact", e.target.checked)} className="h-4 w-4 accent-[var(--brand)]" data-testid="emp-is-sales-contact" />
              Sales Contact (appears in signup / quote attribution dropdowns)
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" data-testid="emp-commission-eligible-label">
              <input type="checkbox" checked={!!form.commission_eligible} onChange={(e) => set("commission_eligible", e.target.checked)} className="h-4 w-4 accent-[var(--brand)]" data-testid="emp-commission-eligible" />
              Commission Eligible
            </label>
          </div>
        </div>
        {!isEdit && templates.length > 0 && (
          <div className="mt-4">
            <Label className="text-xs font-medium text-foreground/70">Assign Onboarding Template</Label>
            <select value={form.onboarding_template_id} onChange={(e) => set("onboarding_template_id", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm text-foreground px-3" data-testid="emp-onboarding_template">
              <option value="">— Skip —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex gap-3 mt-6">
          <Button type="submit" disabled={saving} className="h-9 bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90 text-sm" data-testid="emp-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{isEdit ? "Save Changes" : "Create Employee"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="h-9 text-sm">Cancel</Button>
        </div>
      </form>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  EMPLOYEE DETAIL VIEW
// ═══════════════════════════════════════════════════════════════
const StatusChip = ({ ok, label, warn }) => (
  <span data-testid={`chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${ok === true ? "bg-green-100 text-green-700" : ok === false ? (warn ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700") : "bg-muted text-foreground/60"}`}>{label}</span>
);

const LoginEmailStatus = ({ employeeId }) => {
  const [d, setD] = useState(null);
  const [hist, setHist] = useState([]);
  const load = () => {
    api(`/${employeeId}/diagnostics`).then(setD).catch(() => {});
    api(`/${employeeId}/email-history`).then((r) => setHist(r.history || [])).catch(() => {});
  };
  useEffect(load, [employeeId]);
  if (!d) return null;
  const wes = d.welcome_email_status;
  const wesLabel = wes === "sent" ? "Welcome Email Sent" : wes === "failed" ? "Welcome Email Failed" : wes === "queued" ? "Welcome Email Queued" : "Welcome Email Not Sent";
  const info = [
    ["Employee ID", d.employee_id],
    ["Employee Email", d.employee_email || "—"],
    ["App User ID", d.app_user_id || "—"],
    ["App User Email", d.app_user_email || "—"],
    ["Account Type", d.account_type || "—"],
    ["Invite Status", d.invite_status],
    ["Session Routing Target", d.session_routing_target],
    ["Last Resend Message ID", d.last_resend_message_id || "—"],
    ["Last Login", d.last_login ? new Date(d.last_login).toLocaleString() : "Never"],
    ["Last Password Reset", d.last_password_reset_at ? new Date(d.last_password_reset_at).toLocaleString() : "—"],
  ];
  return (
    <Card className="border-border bg-card p-5 mb-6" data-testid="employee-login-status">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">Login &amp; Email Status</h3>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={load} data-testid="diag-refresh">Refresh</Button>
      </div>
      {d.auth_user_exists && !d.mapping_ok && (
        <div className="mb-3 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="diag-mapping-error">
          ⚠ Identity mismatch: the login account email ({d.app_user_email || "—"}) does not match the employee email ({d.employee_email || "—"}). Password reset is blocked until the mapping is repaired.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <StatusChip ok={d.employee_active} label={d.employee_active ? "Employee Active" : "Employee Inactive"} />
        <StatusChip ok={d.auth_user_exists} label={d.auth_user_exists ? "Auth User Linked" : "Auth User Missing"} />
        <StatusChip ok={d.mapping_ok} label={d.mapping_ok ? "Identity Verified" : "Identity Mismatch"} />
        <StatusChip ok={d.login_enabled} label={d.login_enabled ? "Login Enabled" : "Login Disabled"} />
        <StatusChip ok={wes === "sent" ? true : wes === "failed" ? false : null} warn={wes !== "failed"} label={wesLabel} />
        {d.password_reset_required && <StatusChip ok={false} warn label="Password Reset Required" />}
        <StatusChip ok={null} label={`Role: ${d.role || "—"}`} />
        {d.is_sales_contact && <StatusChip ok={true} label="Sales Contact" />}
        {d.commission_eligible && <StatusChip ok={true} label="Commission Eligible" />}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 text-xs">
        {info.map(([l, v]) => (
          <div key={l} className="flex justify-between gap-2"><span className="text-foreground/45">{l}</span><span className="text-foreground/80 text-right break-all">{v}</span></div>
        ))}
      </div>
      {d.last_email_error && <div className="mt-3 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="diag-email-error">Last email error: {d.last_email_error}</div>}
      <div className="mt-4">
        <div className="text-xs font-medium text-foreground/70 mb-1">Email Delivery History</div>
        {hist.length === 0 ? <div className="text-[11px] text-foreground/40">No email attempts yet.</div> : (
          <div className="max-h-40 overflow-auto rounded border border-border">
            <table className="w-full text-left text-[11px]">
              <thead className="text-foreground/45 uppercase"><tr><th className="p-1.5">Type</th><th className="p-1.5">Status</th><th className="p-1.5">Msg ID</th><th className="p-1.5">When</th></tr></thead>
              <tbody>
                {hist.map((h) => (
                  <tr key={h.id} className="border-t border-border/40" data-testid={`email-hist-${h.id}`}>
                    <td className="p-1.5">{h.email_type}</td>
                    <td className={`p-1.5 font-medium ${h.status === "sent" ? "text-green-600" : h.status === "failed" ? "text-red-600" : "text-foreground/60"}`}>{h.status}</td>
                    <td className="p-1.5 break-all">{h.resend_provider_message_id || "—"}</td>
                    <td className="p-1.5">{h.created_at ? new Date(h.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
};
const adminTestLab = (path, opts = {}) =>
  fetch(`${BACKEND}/api/admin/test-lab${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", "x-user-role": "admin", "x-user-id": "dirk", ...opts.headers },
  }).then(async (r) => {
    if (!r.ok) { let d = `API error ${r.status}`; try { const j = await r.json(); if (j?.detail) d = j.detail; } catch {} throw new Error(d); }
    return r.json();
  });

const TestLabToggle = ({ employeeId }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const load = () => adminTestLab(`/settings/${employeeId}`).then(setS).catch(() => {});
  useEffect(() => { load(); }, [employeeId]);
  if (!s) return null;
  const toggle = async () => {
    setBusy(true);
    try { await adminTestLab(`/settings/${employeeId}`, { method: "POST", body: JSON.stringify({ enabled: !s.enabled }) }); toast.success(`Test Lab ${!s.enabled ? "enabled" : "disabled"}`); load(); }
    catch (e) { toast.error(e.message || "Could not update"); } finally { setBusy(false); }
  };
  const purge = async () => {
    setBusy(true);
    try { const r = await adminTestLab(`/purge`, { method: "POST", body: JSON.stringify({ employee_id: employeeId }) }); toast.success(`Deleted ${r.runs} test run(s)`); setConfirmPurge(false); load(); }
    catch (e) { toast.error(e.message || "Purge failed"); } finally { setBusy(false); }
  };
  return (
    <Card className="border-border bg-card p-5 mb-6" data-testid="test-lab-toggle-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            Sales Test Lab
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.enabled ? "bg-green-100 text-green-700" : "bg-muted text-foreground/60"}`} data-testid="test-lab-status-chip">{s.enabled ? "Enabled" : "Disabled"}</span>
          </h3>
          <p className="mt-1 text-xs text-foreground/50">Lets this employee generate clearly-tagged test data (leads, portfolios, schools, users) to exercise the sales workflow. {s.run_count > 0 ? `${s.run_count} run(s) created.` : ""}</p>
        </div>
        <Button size="sm" onClick={toggle} disabled={busy} className={s.enabled ? "bg-red-500/90 hover:bg-red-500 text-white h-8 text-xs" : "bg-[var(--brand)] text-white h-8 text-xs"} data-testid="test-lab-toggle-btn">
          {busy ? "…" : s.enabled ? "Disable Test Lab" : "Enable Test Lab"}
        </Button>
      </div>
      {s.run_count > 0 && (
        <div className="mt-4 pt-3 border-t border-border/50">
          {!confirmPurge ? (
            <Button size="sm" variant="outline" onClick={() => setConfirmPurge(true)} className="h-7 text-xs text-red-600" data-testid="test-lab-purge-btn"><Trash2 className="h-3.5 w-3.5 mr-1" />Delete all this employee's test data</Button>
          ) : (
            <div className="flex items-center gap-2 text-xs" data-testid="test-lab-purge-confirm">
              <span className="text-foreground/70">Delete ALL {s.run_count} test run(s) and their records?</span>
              <Button size="sm" onClick={purge} disabled={busy} className="h-7 text-xs bg-red-500 text-white" data-testid="test-lab-purge-confirm-btn">Yes, delete</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmPurge(false)} className="h-7 text-xs">Cancel</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};



const CommissionSettings = ({ employeeId }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => fetch(`${BACKEND}/api/admin/commission/employee/${employeeId}`, { headers: { "x-user-role": "admin", "x-user-id": "dirk" } }).then((r) => r.json()).then(setC).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [employeeId]);
  if (!c) return null;
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/commission/employee/${employeeId}`, {
        method: "POST", headers: { "Content-Type": "application/json", "x-user-role": "admin", "x-user-id": "dirk" },
        body: JSON.stringify({ commission_eligible: !!c.commission_eligible, commission_rate_percent: Number(c.commission_rate_percent || 0),
          commission_effective_start_date: c.commission_effective_start_date || null, commission_effective_end_date: c.commission_effective_end_date || null,
          commission_notes: c.commission_notes || null, commission_status: c.commission_status || null }) });
      if (!r.ok) throw new Error("Save failed");
      toast.success("Commission settings saved"); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="border-border bg-card p-5 mb-6" data-testid="commission-settings-card">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3"><DollarSign className="h-4 w-4" />Sales Commission</h3>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-2 text-sm text-foreground/70" data-testid="commission-eligible">
          <input type="checkbox" checked={!!c.commission_eligible} onChange={(e) => set("commission_eligible", e.target.checked)} /> Commission eligible
        </label>
        <div>
          <Label className="text-[11px] text-foreground/50">Commission Rate %</Label>
          <Input type="number" min={0} max={100} step="0.5" value={c.commission_rate_percent ?? 0} onChange={(e) => set("commission_rate_percent", e.target.value)} className="mt-1 h-9 text-sm" data-testid="commission-rate-input" />
        </div>
        <div>
          <Label className="text-[11px] text-foreground/50">Effective Start</Label>
          <Input type="date" value={c.commission_effective_start_date || ""} onChange={(e) => set("commission_effective_start_date", e.target.value)} className="mt-1 h-9 text-sm" data-testid="commission-start" />
        </div>
        <div>
          <Label className="text-[11px] text-foreground/50">Effective End (optional)</Label>
          <Input type="date" value={c.commission_effective_end_date || ""} onChange={(e) => set("commission_effective_end_date", e.target.value)} className="mt-1 h-9 text-sm" data-testid="commission-end" />
        </div>
        <div>
          <Label className="text-[11px] text-foreground/50">Status</Label>
          <select value={c.commission_status || "Not Eligible"} onChange={(e) => set("commission_status", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border text-sm px-2 bg-background" data-testid="commission-status">
            {["Not Eligible", "Pending Revenue", "Calculated", "Under Review", "Approved", "Paid", "Adjusted", "Rejected"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <Label className="text-[11px] text-foreground/50">Notes</Label>
          <Input value={c.commission_notes || ""} onChange={(e) => set("commission_notes", e.target.value)} className="mt-1 h-9 text-sm" data-testid="commission-notes" />
        </div>
      </div>
      <Button size="sm" onClick={save} disabled={busy} className="mt-4 bg-[var(--brand)] text-white h-8 text-xs" data-testid="commission-save-btn">{busy ? "…" : "Save Commission Settings"}</Button>
    </Card>
  );
};

const EmployeePhotoManager = ({ employeeId, name, photoUrl, onChange }) => {
  const [url, setUrl] = useState(photoUrl || null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setUrl(photoUrl || null); }, [photoUrl]);
  const inits = (name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "E";
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("target_type", "employee"); fd.append("target_id", employeeId); fd.append("file", f);
      const r = await fetch(`${BACKEND}/api/assets/admin/profile-photo`, {
        method: "POST", headers: { "x-user-role": "admin", "x-user-id": "dirk" }, body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Upload failed");
      const d = await r.json();
      setUrl(d.profile_photo_url); onChange?.(d.profile_photo_url); toast.success("Photo updated");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/assets/admin/profile-photo?target_type=employee&target_id=${employeeId}`, {
        method: "DELETE", headers: { "x-user-role": "admin", "x-user-id": "dirk" } });
      if (!r.ok) throw new Error("Could not remove");
      setUrl(null); onChange?.(null); toast.success("Photo removed");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3" data-testid="employee-photo-manager">
      {url ? (
        <img src={`${BACKEND}${url}`} alt={`${name} profile photo`} className="h-14 w-14 rounded-full object-cover border border-border" data-testid="employee-photo-img" />
      ) : (
        <div className="h-14 w-14 rounded-full grid place-items-center bg-[var(--brand)] text-white font-semibold" data-testid="employee-photo-initials">{inits}</div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} data-testid="employee-photo-input" />
      <div className="flex flex-col gap-1">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="employee-photo-upload-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>{url ? "Replace" : "Upload"} Photo</>}
        </Button>
        {url && <button onClick={remove} disabled={busy} className="text-[11px] text-red-600 hover:underline text-left" data-testid="employee-photo-remove-btn">Remove</button>}
      </div>
    </div>
  );
};

const EmployeeDetail = ({ employeeId, onBack, roles }) => {
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailTab, setDetailTab] = useState("summary");

  useEffect(() => {
    setLoading(true);
    api(`/${employeeId}`).then((d) => { setEmp(d); setLoading(false); }).catch(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="text-center py-10 text-foreground/40"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!emp) return <div className="text-center py-10 text-foreground/40">Employee not found</div>;

  const roleName = emp.role?.name || roles.find((r) => r.id === emp.role_id)?.name || "Unknown";
  const activeAssigns = (emp.assignments || []).filter((a) => a.status === "active");
  const totalRev = activeAssigns.reduce((s, a) => s + (a.revenue_credit || a.estimated_value || 0), 0);

  return (
    <div data-testid="employee-detail">
      <Button variant="ghost" size="sm" onClick={onBack} className="h-8 mb-4"><ArrowLeft className="h-4 w-4 mr-1" />Back to Directory</Button>
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <EmployeePhotoManager employeeId={employeeId} name={`${emp.first_name} ${emp.last_name}`} photoUrl={emp.profile_photo_url} onChange={(u) => setEmp((p) => ({ ...p, profile_photo_url: u }))} />
          <div>
            <h2 className="text-xl font-semibold text-foreground">{emp.first_name} {emp.last_name}</h2>
            <div className="text-sm text-foreground/50 mt-0.5">{emp.title} — {emp.department}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ResetPasswordButton employee={emp} />
          <WelcomeEmailButton employee={emp} />
          <Badge className={`text-xs ${STATUS_COLORS[emp.status]}`}>{emp.status}</Badge>
        </div>
      </div>
      {emp.last_welcome_email_sent_at && (
        <div className="text-[11px] text-foreground/40 -mt-4 mb-4" data-testid="emp-welcome-status">
          Welcome email: {emp.welcome_email_status || "—"} · last sent {new Date(emp.last_welcome_email_sent_at).toLocaleString()}
          {emp.last_resend_provider_message_id ? ` · Resend id ${emp.last_resend_provider_message_id}` : ""}
        </div>
      )}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-6">
        <SummaryCard label="Active Assignments" value={activeAssigns.length} icon={Briefcase} />
        <SummaryCard label="Total Revenue" value={fmt.format(totalRev)} icon={DollarSign} accent />
        <SummaryCard label="Billing Rate" value={`$${emp.billing_rate}/hr`} icon={BarChart3} />
        <SummaryCard label="Role" value={roleName} icon={Shield} />
      </div>

      <LoginEmailStatus employeeId={employeeId} />
      <CommissionSettings employeeId={employeeId} />
      <TestLabToggle employeeId={employeeId} />

      <Tabs value={detailTab} onValueChange={setDetailTab}>
        <TabsList className="bg-muted border border-border rounded-lg h-9 p-1 gap-1">
          {["summary", "assignments", "onboarding", "activity"].map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-md text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm capitalize">{t === "assignments" ? `Assignments (${(emp.assignments || []).length})` : t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <Card className="border-border bg-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              {[["Email", emp.email], ["Phone", emp.phone || "—"], ["Department", emp.department || "—"], ["Manager", emp.manager ? `${emp.manager.first_name} ${emp.manager.last_name}` : "—"], ["Start Date", emp.start_date], ["Employment Type", (emp.employment_type || "").replace("_", " ")], ["Cost Rate", `$${emp.cost_rate}/hr`], ["Last Login", emp.last_login ? new Date(emp.last_login).toLocaleDateString() : "Never"]].map(([label, val]) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-foreground/50">{label}</span>
                  <span className="text-foreground font-medium">{val}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="mt-4">
          <Card className="border-border bg-card p-4">
            {(emp.assignments || []).length === 0 ? (
              <div className="text-center py-8 text-foreground/40 text-sm">No assignments yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-foreground/50 text-xs"><th className="text-left py-2 pr-4">Type</th><th className="text-left py-2 pr-4">Name</th><th className="text-left py-2 pr-4">Role</th><th className="text-right py-2 pr-4">Value</th><th className="text-right py-2 pr-4">Credit</th><th className="text-left py-2">Status</th></tr></thead>
                  <tbody>
                    {emp.assignments.map((a) => (
                      <tr key={a.id} className="border-b border-border/50">
                        <td className="py-2.5 pr-4"><Badge variant="outline" className="text-[10px]">{a.assignment_type.replace("_", " ")}</Badge></td>
                        <td className="py-2.5 pr-4 text-foreground">{a.assignment_name}</td>
                        <td className="py-2.5 pr-4 text-foreground/60">{a.assigned_role || "—"}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{fmt.format(a.estimated_value || 0)}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums font-medium">{fmt.format(a.revenue_credit || 0)}</td>
                        <td className="py-2.5"><Badge className={`text-[10px] ${a.status === "active" ? STATUS_COLORS.active : STATUS_COLORS.deactivated}`}>{a.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          {emp.onboarding ? (
            <Card className="border-border bg-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div><div className="text-sm font-semibold text-foreground">Onboarding Progress</div><div className="text-xs text-foreground/40 mt-0.5">Status: {emp.onboarding.status}</div></div>
                <div className="text-2xl font-bold text-[var(--brand)]">{emp.onboarding.progress_percent}%</div>
              </div>
              <div className="w-full h-2 bg-foreground/10 rounded-full mb-5"><div className="h-full bg-[var(--brand)] rounded-full transition-all" style={{ width: `${emp.onboarding.progress_percent}%` }} /></div>
              <div className="space-y-2">
                {(emp.onboarding.tasks || []).map((t) => (
                  <div key={t.id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${t.status === "completed" ? "border-emerald-500/20 bg-emerald-500/[0.03]" : "border-border bg-muted/50"}`}>
                    <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${t.status === "completed" ? "bg-emerald-500/20 text-emerald-500" : "bg-foreground/10 text-foreground/30"}`}>
                      {t.status === "completed" ? <Check className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0"><div className="text-sm text-foreground">{t.task_name}</div><div className="text-[10px] text-foreground/40">{t.category} · {t.owner_type} · Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</div></div>
                    {t.required_flag && <Badge variant="outline" className="text-[10px] shrink-0">Required</Badge>}
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="border-border bg-card p-8 text-center"><ClipboardList className="h-8 w-8 text-foreground/20 mx-auto mb-3" /><div className="text-sm text-foreground/40">No onboarding assigned</div></Card>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="border-border bg-card p-4">
            {(emp.activity_log || []).length === 0 ? (
              <div className="text-center py-8 text-foreground/40 text-sm">No activity recorded yet</div>
            ) : (
              <div className="space-y-2">
                {emp.activity_log.map((a) => (
                  <div key={a.id} className="flex gap-3 py-2 border-b border-border/40 last:border-0">
                    <div className="h-2 w-2 rounded-full bg-[var(--brand)]/60 mt-2 shrink-0" />
                    <div><div className="text-sm text-foreground">{a.activity_description}</div><div className="text-xs text-foreground/40 mt-0.5">{a.activity_type} · {new Date(a.created_at).toLocaleString()} · by {a.performed_by}</div></div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ONBOARDING TAB (Active + Templates Management)
// ═══════════════════════════════════════════════════════════════
const OnboardingTab = () => {
  const [subView, setSubView] = useState("active"); // active | templates
  const [onboardings, setOnboardings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(null); // template_id
  const [taskForm, setTaskForm] = useState({ task_name: "", description: "", category: "general", owner_type: "employee", due_offset_days: 7, required_flag: true, sort_order: 0 });
  const [templateForm, setTemplateForm] = useState({ name: "", role_id: "", description: "" });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [onbs, tpls, rls] = await Promise.all([api("/onboarding/active"), api("/onboarding/templates"), api("/roles")]);
      setOnboardings(onbs);
      setTemplates(tpls);
      setRoles(rls);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name) { toast.error("Template name is required"); return; }
    setSaving(true);
    try {
      if (editTemplate) {
        await api(`/onboarding/templates/${editTemplate.id}`, { method: "PUT", body: JSON.stringify(templateForm) });
        toast.success("Template updated");
      } else {
        await api("/onboarding/templates", { method: "POST", body: JSON.stringify(templateForm) });
        toast.success("Template created");
      }
      setShowTemplateForm(false);
      setEditTemplate(null);
      setTemplateForm({ name: "", role_id: "", description: "" });
      loadData();
    } catch { toast.error("Failed to save template"); }
    finally { setSaving(false); }
  };

  const handleDeleteTemplate = async (tplId) => {
    if (!window.confirm("Delete this template and all its tasks?")) return;
    await api(`/onboarding/templates/${tplId}`, { method: "DELETE" });
    toast.success("Template deleted");
    loadData();
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!taskForm.task_name) { toast.error("Task name is required"); return; }
    setSaving(true);
    try {
      await api(`/onboarding/templates/${showTaskForm}/tasks`, { method: "POST", body: JSON.stringify({ ...taskForm, due_offset_days: +taskForm.due_offset_days, sort_order: +taskForm.sort_order }) });
      toast.success("Task added");
      setShowTaskForm(null);
      setTaskForm({ task_name: "", description: "", category: "general", owner_type: "employee", due_offset_days: 7, required_flag: true, sort_order: 0 });
      loadData();
    } catch { toast.error("Failed to add task"); }
    finally { setSaving(false); }
  };

  const handleDeleteTask = async (tplId, taskId) => {
    if (!window.confirm("Delete this task?")) return;
    await api(`/onboarding/templates/${tplId}/tasks/${taskId}`, { method: "DELETE" });
    toast.success("Task deleted");
    loadData();
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>;

  return (
    <div data-testid="onboarding-tab">
      {/* Sub-navigation */}
      <div className="flex gap-2 mb-4">
        <Button variant={subView === "active" ? "default" : "outline"} size="sm" onClick={() => setSubView("active")} className="h-8 text-xs" data-testid="onb-sub-active">
          <ClipboardList className="h-3.5 w-3.5 mr-1" />Active ({onboardings.length})
        </Button>
        <Button variant={subView === "templates" ? "default" : "outline"} size="sm" onClick={() => setSubView("templates")} className="h-8 text-xs" data-testid="onb-sub-templates">
          <FileText className="h-3.5 w-3.5 mr-1" />Templates ({templates.length})
        </Button>
      </div>

      {/* Active Onboardings */}
      {subView === "active" && (
        <div data-testid="onboarding-list">
          {onboardings.length === 0 ? (
            <Card className="border-border bg-card p-10 text-center"><ClipboardList className="h-8 w-8 text-foreground/20 mx-auto mb-3" /><div className="text-sm text-foreground/50">No employees currently onboarding</div></Card>
          ) : (
            <div className="space-y-3">
              {onboardings.map((o) => (
                <Card key={o.id} className="border-border bg-card p-4">
                  <div className="flex items-center justify-between">
                    <div><div className="text-sm font-medium text-foreground">{o.employee?.first_name} {o.employee?.last_name}</div><div className="text-xs text-foreground/40 mt-0.5">Started: {o.started_at ? new Date(o.started_at).toLocaleDateString() : "—"} · {o.days_in_onboarding || 0} days</div></div>
                    <div className="flex items-center gap-3">
                      <Badge className={`text-[10px] ${RISK_COLORS[o.risk_status] || RISK_COLORS.on_track}`}>{(o.risk_status || "on_track").replace("_", " ")}</Badge>
                      <div className="text-right"><div className="text-lg font-bold text-foreground">{o.progress_percent}%</div><div className="text-[10px] text-foreground/40">{o.tasks_completed}/{o.tasks_total} tasks</div></div>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-foreground/10 rounded-full mt-3"><div className="h-full bg-[var(--brand)] rounded-full transition-all" style={{ width: `${o.progress_percent}%` }} /></div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Templates Management */}
      {subView === "templates" && (
        <div data-testid="templates-manager">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => { setEditTemplate(null); setTemplateForm({ name: "", role_id: "", description: "" }); setShowTemplateForm(true); }} className="h-8 text-xs bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90" data-testid="add-template-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />New Template
            </Button>
          </div>

          {/* Template Form Modal */}
          {showTemplateForm && (
            <Card className="border-border bg-card p-5 mb-4" data-testid="template-form">
              <h3 className="text-sm font-semibold text-foreground mb-3">{editTemplate ? "Edit Template" : "New Onboarding Template"}</h3>
              <form onSubmit={handleSaveTemplate} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-foreground/70">Template Name *</Label>
                    <Input value={templateForm.name} onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 h-9 text-sm border-border bg-background" data-testid="tpl-name" placeholder="e.g. Sales Onboarding" />
                  </div>
                  <div>
                    <Label className="text-xs text-foreground/70">Target Role</Label>
                    <select value={templateForm.role_id} onChange={(e) => setTemplateForm((f) => ({ ...f, role_id: e.target.value }))} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm text-foreground px-3" data-testid="tpl-role">
                      <option value="">— Any Role —</option>
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-foreground/70">Description</Label>
                  <Textarea value={templateForm.description} onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))} className="mt-1 min-h-[50px] text-sm border-border bg-background" data-testid="tpl-description" />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving} size="sm" className="h-8 text-xs bg-[var(--brand)] text-white" data-testid="tpl-save-btn">
                    {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}{editTemplate ? "Update" : "Create"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => { setShowTemplateForm(false); setEditTemplate(null); }} className="h-8 text-xs">Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {/* Templates List */}
          {templates.length === 0 ? (
            <Card className="border-border bg-card p-10 text-center"><FileText className="h-8 w-8 text-foreground/20 mx-auto mb-3" /><div className="text-sm text-foreground/50">No templates yet</div></Card>
          ) : (
            <div className="space-y-3">
              {templates.map((tpl) => (
                <Card key={tpl.id} className="border-border bg-card" data-testid={`template-${tpl.id}`}>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-foreground">{tpl.name}</div>
                      <div className="text-xs text-foreground/40">{tpl.description || "No description"} · {(tpl.tasks || []).length} tasks</div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditTemplate(tpl); setTemplateForm({ name: tpl.name, role_id: tpl.role_id || "", description: tpl.description || "" }); setShowTemplateForm(true); }} data-testid={`edit-tpl-${tpl.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteTemplate(tpl.id)} data-testid={`delete-tpl-${tpl.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Tasks */}
                  {(tpl.tasks || []).length > 0 && (
                    <div className="border-t border-border/50 px-4 py-2 space-y-1">
                      {tpl.tasks.map((task, i) => (
                        <div key={task.id} className="flex items-center gap-2 py-1 text-xs group">
                          <span className="text-foreground/30 w-5 text-right">{i + 1}.</span>
                          <span className="text-foreground flex-1">{task.task_name}</span>
                          <Badge variant="outline" className="text-[9px]">{task.category}</Badge>
                          <span className="text-foreground/40">{task.due_offset_days}d</span>
                          {task.required_flag && <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-600">Req</Badge>}
                          <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteTask(tpl.id, task.id)}>
                            <Trash2 className="h-2.5 w-2.5 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Task */}
                  {showTaskForm === tpl.id ? (
                    <div className="border-t border-border/50 p-4" data-testid="task-form">
                      <form onSubmit={handleAddTask} className="space-y-2">
                        <div className="grid gap-2 sm:grid-cols-3">
                          <div><Label className="text-[10px] text-foreground/60">Task Name *</Label><Input value={taskForm.task_name} onChange={(e) => setTaskForm((f) => ({ ...f, task_name: e.target.value }))} className="h-8 text-xs" data-testid="task-name" /></div>
                          <div><Label className="text-[10px] text-foreground/60">Category</Label>
                            <select value={taskForm.category} onChange={(e) => setTaskForm((f) => ({ ...f, category: e.target.value }))} className="h-8 w-full rounded-md border border-border bg-background text-xs px-2" data-testid="task-category">
                              {["general", "Account Setup", "Compliance", "Platform Access", "Internal Training", "Shadowing", "Role-Specific Training", "Manager Review", "First Assignment"].map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div><Label className="text-[10px] text-foreground/60">Due Offset (days)</Label><Input type="number" value={taskForm.due_offset_days} onChange={(e) => setTaskForm((f) => ({ ...f, due_offset_days: e.target.value }))} className="h-8 text-xs" data-testid="task-offset" /></div>
                        </div>
                        <div className="flex gap-2 items-center">
                          <label className="flex items-center gap-1.5 text-xs text-foreground/60"><input type="checkbox" checked={taskForm.required_flag} onChange={(e) => setTaskForm((f) => ({ ...f, required_flag: e.target.checked }))} />Required</label>
                          <select value={taskForm.owner_type} onChange={(e) => setTaskForm((f) => ({ ...f, owner_type: e.target.value }))} className="h-7 rounded-md border border-border bg-background text-xs px-2">
                            <option value="employee">Employee</option><option value="manager">Manager</option>
                          </select>
                          <div className="ml-auto flex gap-1">
                            <Button type="submit" disabled={saving} size="sm" className="h-7 text-[10px] bg-[var(--brand)] text-white" data-testid="task-save-btn">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add Task"}</Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => setShowTaskForm(null)} className="h-7 text-[10px]">Cancel</Button>
                          </div>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="border-t border-border/50 px-4 py-2">
                      <Button variant="ghost" size="sm" onClick={() => setShowTaskForm(tpl.id)} className="h-7 text-[10px] text-foreground/50 hover:text-foreground" data-testid={`add-task-${tpl.id}`}>
                        <Plus className="h-3 w-3 mr-1" />Add Task
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ROLES & PERMISSIONS
// ═══════════════════════════════════════════════════════════════
const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api("/roles").then((d) => { setRoles(d); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const togglePerm = async (role, perm) => {
    const newPerms = { ...role.permissions, [perm]: !role.permissions[perm] };
    await api(`/roles/${role.id}`, { method: "PUT", body: JSON.stringify({ permissions: newPerms }) });
    setRoles(roles.map((r) => (r.id === role.id ? { ...r, permissions: newPerms } : r)));
    toast.success(`Permission updated for ${role.name}`);
  };

  const PERM_LABELS = {
    view_employees: "View Employees", create_employees: "Create Employees", edit_employees: "Edit Employees",
    deactivate_employees: "Deactivate", view_onboarding: "View Onboarding", edit_onboarding: "Edit Onboarding",
    manage_onboarding_templates: "Manage Templates", view_assignments: "View Assignments", assign_work: "Assign Work",
    view_revenue: "View Revenue", view_only_own_data: "Own Data Only", access_super_user_config: "Super Config",
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>;

  return (
    <div data-testid="roles-permissions">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-border"><th className="text-left py-2 pr-4 text-foreground/50 font-medium sticky left-0 bg-background z-10">Permission</th>{roles.map((r) => <th key={r.id} className="text-center py-2 px-2 text-foreground/70 font-medium min-w-[80px]">{r.name}</th>)}</tr></thead>
          <tbody>
            {Object.entries(PERM_LABELS).map(([perm, label]) => (
              <tr key={perm} className="border-b border-border/40">
                <td className="py-2 pr-4 text-foreground/60 sticky left-0 bg-background z-10">{label}</td>
                {roles.map((r) => (
                  <td key={r.id} className="text-center py-2 px-2">
                    <button onClick={() => togglePerm(r, perm)} className={`h-6 w-6 rounded-md mx-auto flex items-center justify-center transition-colors ${r.permissions[perm] ? "bg-emerald-500/20 text-emerald-500" : "bg-foreground/5 text-foreground/15 hover:bg-foreground/10"}`} data-testid={`perm-${r.name}-${perm}`}>
                      {r.permissions[perm] && <Check className="h-3 w-3" />}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ASSIGNMENTS TAB (with Create Form)
// ═══════════════════════════════════════════════════════════════
const AssignmentsList = ({ allEmployees }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: "", status: "" });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    employee_id: "", assignment_type: "project", assignment_name: "",
    customer_id: "", assigned_role: "", start_date: new Date().toISOString().split("T")[0],
    end_date: "", planned_hours: 0, estimated_value: 0,
    cost_rate: 0, billing_rate: 0,
  });

  useEffect(() => {
    api("/lookup/customers").then(setCustomers).catch(() => {});
  }, []);

  const loadAssignments = useCallback(() => {
    const params = new URLSearchParams();
    if (filter.type) params.set("assignment_type", filter.type);
    if (filter.status) params.set("status", filter.status);
    api(`/assignments/all?${params}`).then((d) => { setAssignments(d); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const ASSIGNMENT_ROLES = ["Lead Consultant", "Supporting Consultant", "Project Manager", "AI Specialist", "Data Analyst", "Technical Lead"];

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.assignment_name) { toast.error("Employee and assignment name are required"); return; }
    setSaving(true);
    try {
      await api("/assignments", { method: "POST", body: JSON.stringify({ ...form, planned_hours: +form.planned_hours, estimated_value: +form.estimated_value, cost_rate: +form.cost_rate, billing_rate: +form.billing_rate }) });
      toast.success("Assignment created");
      setShowForm(false);
      setForm({ employee_id: "", assignment_type: "project", assignment_name: "", customer_id: "", assigned_role: "", start_date: new Date().toISOString().split("T")[0], end_date: "", planned_hours: 0, estimated_value: 0, cost_rate: 0, billing_rate: 0 });
      loadAssignments();
    } catch { toast.error("Failed to create assignment"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>;

  return (
    <div data-testid="assignments-list">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <select value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))} className="h-8 rounded-md border border-border bg-background text-xs text-foreground px-2" data-testid="asgn-type-filter">
          <option value="">All Types</option><option value="free_assessment">Free Assessment</option><option value="detailed_assessment">In-Depth Assessment</option><option value="project">Project</option><option value="retainer">Retainer</option><option value="internal">Internal</option>
        </select>
        <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} className="h-8 rounded-md border border-border bg-background text-xs text-foreground px-2" data-testid="asgn-status-filter">
          <option value="">All Statuses</option><option value="active">Active</option><option value="completed">Completed</option><option value="paused">Paused</option>
        </select>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="h-8 text-xs bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90 ml-auto" data-testid="add-assignment-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />New Assignment
        </Button>
      </div>

      {/* Assignment Creation Form */}
      {showForm && (
        <Card className="border-border bg-card p-5 mb-4" data-testid="assignment-form">
          <h3 className="text-sm font-semibold text-foreground mb-3">Create Assignment</h3>
          <form onSubmit={handleCreate}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label className="text-xs text-foreground/70">Employee *</Label>
                <select value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-3" data-testid="asgn-employee">
                  <option value="">-- Select Employee --</option>
                  {(allEmployees || []).map((emp) => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Type *</Label>
                <select value={form.assignment_type} onChange={(e) => setForm((f) => ({ ...f, assignment_type: e.target.value }))} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-3" data-testid="asgn-type">
                  <option value="free_assessment">Free Assessment</option><option value="detailed_assessment">In-Depth Assessment</option><option value="project">Project</option><option value="retainer">Retainer</option><option value="internal">Internal</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Customer / Account *</Label>
                <select value={form.assignment_name} onChange={(e) => { const cust = customers.find((c) => c.company_name === e.target.value); setForm((f) => ({ ...f, assignment_name: e.target.value, customer_id: cust?.prospect_id || "" })); }} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-3" data-testid="asgn-name">
                  <option value="">-- Select Customer --</option>
                  {[...new Map(customers.map((c) => [c.company_name, c])).values()].map((c) => (
                    <option key={c.prospect_id} value={c.company_name}>{c.company_name}{c.contact_name ? ` (${c.contact_name})` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Assigned Role</Label>
                <select value={form.assigned_role} onChange={(e) => setForm((f) => ({ ...f, assigned_role: e.target.value }))} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-3" data-testid="asgn-role">
                  <option value="">-- Select Role --</option>
                  {ASSIGNMENT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="mt-1 h-9 text-sm border-border bg-background" data-testid="asgn-start" />
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Estimated Value ($)</Label>
                <Input type="number" value={form.estimated_value} onChange={(e) => setForm((f) => ({ ...f, estimated_value: e.target.value }))} className="mt-1 h-9 text-sm border-border bg-background" data-testid="asgn-value" />
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Planned Hours</Label>
                <Input type="number" value={form.planned_hours} onChange={(e) => setForm((f) => ({ ...f, planned_hours: e.target.value }))} className="mt-1 h-9 text-sm border-border bg-background" data-testid="asgn-hours" />
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Billing Rate ($/hr)</Label>
                <Input type="number" value={form.billing_rate} onChange={(e) => setForm((f) => ({ ...f, billing_rate: e.target.value }))} className="mt-1 h-9 text-sm border-border bg-background" data-testid="asgn-billing" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button type="submit" disabled={saving} size="sm" className="h-8 text-xs bg-[var(--brand)] text-white" data-testid="asgn-save-btn">
                {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Create Assignment
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)} className="h-8 text-xs">Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {assignments.length === 0 ? (
        <Card className="border-border bg-card p-10 text-center"><Briefcase className="h-8 w-8 text-foreground/20 mx-auto mb-3" /><div className="text-sm text-foreground/50">No assignments found</div></Card>
      ) : (
        <Card className="border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-foreground/50 text-xs"><th className="text-left py-2.5 px-4">Employee</th><th className="text-left py-2.5 px-4">Type</th><th className="text-left py-2.5 px-4">Assignment</th><th className="text-right py-2.5 px-4">Value</th><th className="text-right py-2.5 px-4">Credit</th><th className="text-left py-2.5 px-4">Status</th></tr></thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id} className="border-b border-border/40 hover:bg-muted/30">
                  <td className="py-2.5 px-4 text-foreground font-medium">{a.employee_name}</td>
                  <td className="py-2.5 px-4"><Badge variant="outline" className="text-[10px]">{a.assignment_type.replace("_", " ")}</Badge></td>
                  <td className="py-2.5 px-4 text-foreground/70">{a.assignment_name}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums">{fmt.format(a.estimated_value || 0)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums font-medium text-[var(--brand)]">{fmt.format(a.revenue_credit || 0)}</td>
                  <td className="py-2.5 px-4"><Badge className={`text-[10px] ${a.status === "active" ? STATUS_COLORS.active : STATUS_COLORS.deactivated}`}>{a.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  DEACTIVATED EMPLOYEES
// ═══════════════════════════════════════════════════════════════
const DeactivatedList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api("/deactivated").then((d) => { setEmployees(d); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const handleReactivate = async (empId) => {
    await api(`/${empId}/reactivate`, { method: "POST" });
    setEmployees((prev) => prev.filter((e) => e.id !== empId));
    toast.success("Employee reactivated");
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>;

  return (
    <div data-testid="deactivated-list">
      {employees.length === 0 ? (
        <Card className="border-border bg-card p-10 text-center"><UserX className="h-8 w-8 text-foreground/20 mx-auto mb-3" /><div className="text-sm text-foreground/50">No deactivated employees</div></Card>
      ) : (
        <div className="space-y-3">
          {employees.map((e) => (
            <Card key={e.id} className="border-border bg-card p-4 flex items-center justify-between">
              <div><div className="text-sm font-medium text-foreground">{e.first_name} {e.last_name}</div><div className="text-xs text-foreground/40">{e.deactivation_reason} · Deactivated: {e.deactivated_at ? new Date(e.deactivated_at).toLocaleDateString() : "—"}</div></div>
              <Button size="sm" variant="outline" onClick={() => handleReactivate(e.id)} className="h-8 text-xs" data-testid={`reactivate-${e.id}`}>
                <RotateCcw className="h-3 w-3 mr-1" />Reactivate
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  MAIN EXPORT
// ═══════════════════════════════════════════════════════════════
export default function EmployeeOperations() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [view, setView] = useState("list");
  const [selectedEmp, setSelectedEmp] = useState(null);
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [emps, rls, dash] = await Promise.all([api("/"), api("/roles"), api("/dashboard")]);
      setEmployees(emps);
      setRoles(rls);
      setMetrics(dash);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    return employees.filter((e) => {
      if (search) {
        const q = search.toLowerCase();
        if (!`${e.first_name} ${e.last_name} ${e.email} ${e.title}`.toLowerCase().includes(q)) return false;
      }
      if (roleFilter && e.role_id !== roleFilter) return false;
      return true;
    });
  }, [employees, search, roleFilter]);

  const handleDeactivate = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.first_name} ${emp.last_name}?`)) return;
    const reason = window.prompt("Reason for deactivation:");
    if (!reason) return;
    await api(`/${emp.id}/deactivate`, { method: "POST", body: JSON.stringify({ reason }) });
    toast.success(`${emp.first_name} deactivated`);
    loadData();
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Deactivate ${selectedIds.size} employee(s)?`)) return;
    const reason = window.prompt("Reason for deactivation:");
    if (!reason) return;
    try {
      await api("/bulk-deactivate", { method: "POST", body: JSON.stringify({ employee_ids: [...selectedIds], reason }) });
      toast.success(`${selectedIds.size} employee(s) deactivated`);
      setSelectedIds(new Set());
      setBulkMode(false);
      loadData();
    } catch { toast.error("Bulk deactivation failed"); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((e) => e.id)));
  };

  const SUB_TABS = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "directory", label: "Directory", icon: Users },
    { key: "onboarding", label: "Onboarding", icon: ClipboardList },
    { key: "assignments", label: "Assignments", icon: Briefcase },
    { key: "roles", label: "Roles & Permissions", icon: Shield },
    { key: "deactivated", label: "Deactivated", icon: UserX },
  ];

  return (
    <div data-testid="employee-operations">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Users className="h-5 w-5 text-foreground/50" />Employee Operations
        </h1>
        <p className="text-sm text-foreground/40 mt-1">Manage employees, onboarding, assignments, and permissions</p>
      </div>

      {/* Summary Cards */}
      {metrics && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6">
          <SummaryCard label="Active Employees" value={metrics.total_active} icon={Users} />
          <SummaryCard label="In Onboarding" value={metrics.in_onboarding} sub={metrics.overdue_onboarding > 0 ? `${metrics.overdue_onboarding} overdue` : ""} icon={ClipboardList} />
          <SummaryCard label="Revenue Managed" value={fmt.format(metrics.total_revenue_managed)} icon={DollarSign} accent />
          <SummaryCard label="Active Assignments" value={metrics.active_assignments} sub={`${metrics.without_assignments} unassigned`} icon={Briefcase} />
        </div>
      )}

      {/* Sub-Tabs */}
      <div className="flex gap-1 border-b border-border mb-5 overflow-x-auto">
        {SUB_TABS.map((t) => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setView("list"); setSelectedEmp(null); setBulkMode(false); setSelectedIds(new Set()); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === t.key ? "border-[var(--brand)] text-[var(--brand)]" : "border-transparent text-foreground/40 hover:text-foreground/60"}`}
            data-testid={`emp-tab-${t.key}`}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && <><DashboardCharts /><DataMaintenancePanel /><InactiveCleanupPanel /><EmailResendPanel /></>}

      {/* Directory Tab */}
      {activeTab === "directory" && (
        <>
          {view === "form" ? (
            <EmployeeForm employee={selectedEmp} roles={roles} employees={employees} onSave={() => { setView("list"); loadData(); }} onCancel={() => setView("list")} />
          ) : view === "detail" && selectedEmp ? (
            <EmployeeDetail employeeId={selectedEmp.id} onBack={() => { setView("list"); setSelectedEmp(null); }} roles={roles} />
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap gap-3 mb-4 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employees…" className="h-9 pl-9 text-sm border-border bg-background" data-testid="emp-search" />
                </div>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 rounded-md border border-border bg-background text-xs text-foreground px-3" data-testid="emp-role-filter">
                  <option value="">All Roles</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <Button variant={bulkMode ? "secondary" : "outline"} size="sm" onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }} className="h-9 text-xs" data-testid="bulk-mode-btn">
                  {bulkMode ? <><X className="h-3.5 w-3.5 mr-1" />Exit Bulk</> : <><ChevronDown className="h-3.5 w-3.5 mr-1" />Bulk Actions</>}
                </Button>
                <Button size="sm" onClick={() => { setSelectedEmp(null); setView("form"); }} className="h-9 bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90 text-xs ml-auto" data-testid="add-employee-btn">
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />Add Employee
                </Button>
              </div>

              {/* Bulk Action Bar */}
              {bulkMode && selectedIds.size > 0 && (
                <div className="flex items-center gap-3 p-3 mb-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.05]" data-testid="bulk-action-bar">
                  <span className="text-xs text-foreground font-medium">{selectedIds.size} selected</span>
                  <Button size="sm" variant="destructive" onClick={handleBulkDeactivate} className="h-7 text-[10px]" data-testid="bulk-deactivate-btn">
                    <XCircle className="h-3 w-3 mr-1" />Deactivate Selected
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="h-7 text-[10px]">Clear</Button>
                </div>
              )}

              {/* Table */}
              {loading ? (
                <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>
              ) : filtered.length === 0 ? (
                <Card className="border-border bg-card p-10 text-center">
                  <Users className="h-8 w-8 text-foreground/20 mx-auto mb-3" /><div className="text-sm text-foreground/50">No employees found</div>
                  <Button size="sm" className="mt-3 h-8 text-xs bg-[var(--brand)] text-white" onClick={() => { setSelectedEmp(null); setView("form"); }}><UserPlus className="h-3.5 w-3.5 mr-1" />Add First Employee</Button>
                </Card>
              ) : (
                <Card className="border-border bg-card overflow-x-auto" data-testid="employee-directory-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-foreground/50 text-xs">
                        {bulkMode && (
                          <th className="py-2.5 px-3 w-10">
                            <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} className="rounded border-border" data-testid="select-all-cb" />
                          </th>
                        )}
                        <th className="text-left py-2.5 px-4">Name</th>
                        <th className="text-left py-2.5 px-4 hidden md:table-cell">Title</th>
                        <th className="text-left py-2.5 px-4">Role</th>
                        <th className="text-left py-2.5 px-4 hidden lg:table-cell">Department</th>
                        <th className="text-right py-2.5 px-4">Jobs</th>
                        <th className="text-right py-2.5 px-4">Revenue</th>
                        <th className="text-left py-2.5 px-4">Status</th>
                        <th className="text-right py-2.5 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((e) => (
                        <tr key={e.id} className={`border-b border-border/40 hover:bg-muted/30 cursor-pointer ${selectedIds.has(e.id) ? "bg-[var(--brand)]/[0.04]" : ""}`} onClick={() => { if (!bulkMode) { setSelectedEmp(e); setView("detail"); } }} data-testid={`emp-row-${e.id}`}>
                          {bulkMode && (
                            <td className="py-2.5 px-3" onClick={(ev) => ev.stopPropagation()}>
                              <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleSelect(e.id)} className="rounded border-border" data-testid={`cb-${e.id}`} />
                            </td>
                          )}
                          <td className="py-2.5 px-4"><div className="text-foreground font-medium">{e.first_name} {e.last_name}</div><div className="text-xs text-foreground/40">{e.email}</div></td>
                          <td className="py-2.5 px-4 hidden md:table-cell text-foreground/60">{e.title || "—"}</td>
                          <td className="py-2.5 px-4"><Badge variant="outline" className="text-[10px]">{e.role_name}</Badge></td>
                          <td className="py-2.5 px-4 hidden lg:table-cell text-foreground/50">{e.department || "—"}</td>
                          <td className="py-2.5 px-4 text-right tabular-nums">{e.total_assigned_jobs}</td>
                          <td className="py-2.5 px-4 text-right tabular-nums font-medium">{fmt.format(e.total_assigned_revenue)}</td>
                          <td className="py-2.5 px-4"><Badge className={`text-[10px] ${STATUS_COLORS[e.status]}`}>{e.status}</Badge></td>
                          <td className="py-2.5 px-4 text-right" onClick={(ev) => ev.stopPropagation()}>
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedEmp(e); setView("form"); }} data-testid={`edit-${e.id}`}><Pencil className="h-3 w-3" /></Button>
                              {e.status !== "deactivated" && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDeactivate(e)} data-testid={`deactivate-${e.id}`}><XCircle className="h-3 w-3" /></Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {activeTab === "onboarding" && <OnboardingTab />}
      {activeTab === "assignments" && <AssignmentsList allEmployees={employees} />}
      {activeTab === "roles" && <RolesPermissions />}
      {activeTab === "deactivated" && <DeactivatedList />}
    </div>
  );
}
